import {
    Dispatch,
    ReactElement,
    SetStateAction,
    useCallback,
    useMemo,
    useState
} from 'react';
import { ChartContainer } from './chart-container';
import styles from './activity-table.module.scss';
import useElementSize from '@/common/hooks/use-element-size';
import {
    UIAvatar,
    UIErrorState,
    UILoadingState,
    UIMenuItemProps,
    UITableHeaderCell,
    UITableSortDirection,
    UIText
} from '@/ui';
import { ActivityAnalyticsItem } from '@/api';
import {
    TasksAndAlertsTeamActivityProvider,
    useTasksAndAlertsTeamActivity
} from '../providers/tasks-and-alerts-team-activity-provider';
import {
    TasksAndAlertsUserActivityProvider,
    useTasksAndAlertsUserActivity
} from '../providers/tasks-and-alerts-user-activity-provider';
import { blue, red } from '@/ui/styles';
import { useAnalyticsTranslation } from '../hooks';

type DataType = 'teams' | 'users';

type SortBy = 'tasks' | 'alerts';

type ActivityTableProps = {
    type: DataType;
    description: string;
    data: ActivityAnalyticsItem[];
    dateString: string | null;
    rightMenuItems: UIMenuItemProps[];
    rightMenuText: string;
    loading?: boolean;
    error?: boolean;
    sortBy: SortBy;
    setSortBy: Dispatch<SetStateAction<SortBy>>;
    sortDirection: UITableSortDirection;
    setSortDirection: Dispatch<SetStateAction<UITableSortDirection>>;
};

function InternalActivityTable(props: ActivityTableProps): ReactElement {
    const { t } = useAnalyticsTranslation();
    const {
        type,
        description,
        data,
        dateString,
        rightMenuItems,
        rightMenuText,
        loading,
        error,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection
    } = props;

    const [containerDivRef, containerSize] = useElementSize<HTMLDivElement>();
    const [theadRef, theadSize] = useElementSize<HTMLTableSectionElement>();
    const [teamNameTheadRef, teamNameTheadSize] =
        useElementSize<HTMLTableCellElement>();
    const [tasksTheadRef, tasksTheadSize] =
        useElementSize<HTMLTableCellElement>();
    const [alertsTheadRef, alertsTheadSize] =
        useElementSize<HTMLTableCellElement>();

    const title =
        type === 'teams'
            ? t('components.activity_table.title_teams')
            : t('components.activity_table.title_users');
    const itemHeader =
        type === 'teams' ? t('shared.labels.team') : t('shared.labels.user');

    const sortedData = useMemo(() => {
        const sorted = [...data].sort((a, b) => {
            if (sortBy === 'tasks') {
                return sortDirection === 'asc'
                    ? a.tasksCount - b.tasksCount
                    : b.tasksCount - a.tasksCount;
            } else {
                return sortDirection === 'asc'
                    ? a.alertsCount - b.alertsCount
                    : b.alertsCount - a.alertsCount;
            }
        });
        return sorted;
    }, [data, sortBy, sortDirection]);

    const dotIcon = useCallback(
        (color: string) => (
            <div
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    backgroundColor: color
                }}
            />
        ),
        []
    );

    return (
        <ChartContainer
            title={title}
            titleWeight="medium"
            subtitle={dateString ?? '...'}
            rightMenuText={rightMenuText}
            rightMenuItems={rightMenuItems}
            description={description}
        >
            <div
                style={{ display: 'flex', flex: 1, position: 'relative' }}
                ref={containerDivRef}
            >
                <table className={styles.table}>
                    <thead ref={theadRef}>
                        <tr>
                            <th></th>

                            <UITableHeaderCell
                                ref={teamNameTheadRef}
                                text={itemHeader}
                            />

                            <UITableHeaderCell
                                ref={tasksTheadRef}
                                className={
                                    sortBy === 'tasks'
                                        ? styles.activeColumnHeader
                                        : ''
                                }
                                text={t('shared.labels.tasks')}
                                leftNode={dotIcon(blue)}
                                sortable
                                sortDirection={
                                    sortBy === 'tasks'
                                        ? sortDirection
                                        : undefined
                                }
                                onClick={(newSortDirection) => {
                                    if (sortBy === 'tasks') {
                                        setSortDirection(newSortDirection);
                                    } else {
                                        setSortBy('tasks');
                                    }
                                }}
                            />

                            <UITableHeaderCell
                                ref={alertsTheadRef}
                                className={
                                    sortBy === 'alerts'
                                        ? styles.activeColumnHeader
                                        : ''
                                }
                                text={t('shared.labels.alerts')}
                                leftNode={dotIcon(red)}
                                sortable
                                sortDirection={
                                    sortBy === 'alerts'
                                        ? sortDirection
                                        : undefined
                                }
                                onClick={(newSortDirection) => {
                                    if (sortBy === 'alerts') {
                                        setSortDirection(newSortDirection);
                                    } else {
                                        setSortBy('alerts');
                                    }
                                }}
                            />
                        </tr>
                    </thead>
                    <tbody
                        style={{
                            height: containerSize.height - theadSize.height
                        }}
                    >
                        {sortedData.length === 0 && !loading && !error && (
                            <div
                                style={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    display: 'flex',
                                    height: '100%'
                                }}
                            >
                                <UIText textAlign="center">
                                    {t('components.activity_table.no_data')}
                                </UIText>
                            </div>
                        )}
                        {sortedData.map((team, index) => {
                            const isLast = index === sortedData.length - 1;
                            const taskClassNames = [];
                            const alertsClassNames = [];
                            if (sortBy === 'tasks') {
                                taskClassNames.push(styles.activeColumnBody);
                                if (isLast) {
                                    taskClassNames.push(
                                        styles.activeColumnBodyLast
                                    );
                                }
                            } else if (sortBy === 'alerts') {
                                alertsClassNames.push(styles.activeColumnBody);
                                if (isLast) {
                                    alertsClassNames.push(
                                        styles.activeColumnBodyLast
                                    );
                                }
                            }

                            return (
                                <tr key={team.itemId}>
                                    <td className={styles.colIndex}>
                                        <UIText>{index + 1}.</UIText>
                                    </td>
                                    <td
                                        style={{
                                            width: teamNameTheadSize.width
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 16
                                            }}
                                        >
                                            <UIAvatar
                                                img={team.itemImage}
                                                alt={team.itemName}
                                                size={40}
                                            />
                                            <UIText>{team.itemName}</UIText>
                                        </div>
                                    </td>
                                    <td
                                        className={taskClassNames.join(' ')}
                                        style={{
                                            minWidth: tasksTheadSize.width,
                                            textAlign: 'center'
                                        }}
                                    >
                                        <UIText textAlign="center">
                                            {team.tasksCount}
                                        </UIText>
                                    </td>
                                    <td
                                        className={alertsClassNames.join(' ')}
                                        style={{
                                            minWidth: alertsTheadSize.width
                                        }}
                                    >
                                        <UIText textAlign="center">
                                            {team.alertsCount}
                                        </UIText>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {loading && (
                    <div
                        style={{
                            position: 'absolute',
                            pointerEvents: 'none',
                            left: 0,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <UILoadingState />
                    </div>
                )}
                {error && (
                    <div
                        style={{
                            position: 'absolute',
                            pointerEvents: 'none',
                            left: 0,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <UIErrorState
                            title={t('shared.errors.failed_to_load')}
                        />
                    </div>
                )}
            </div>
        </ChartContainer>
    );
}

export function ActivityTable(): ReactElement {
    const { t } = useAnalyticsTranslation();
    const [view, setView] = useState<'teams' | 'users'>('teams');
    const [sortBy, setSortBy] = useState<SortBy>('tasks');
    const [sortDirection, setSortDirection] =
        useState<UITableSortDirection>('desc');

    const rightMenuItems: UIMenuItemProps[] = useMemo(() => {
        const items: UIMenuItemProps[] = [
            {
                text: t('shared.labels.teams'),
                onClick: () => {
                    setView('teams');
                },
                leftIconName: view === 'teams' ? 'check' : undefined
            },
            {
                text: t('shared.labels.users'),
                onClick: () => {
                    setView('users');
                },
                leftIconName: view === 'users' ? 'check' : undefined
            }
        ];

        return items;
    }, [view, t]);

    if (view === 'teams') {
        return (
            <TasksAndAlertsTeamActivityProvider key="teams-activity">
                <TeamActivityTableDataWrapper
                    rightMenuItems={rightMenuItems}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                />
            </TasksAndAlertsTeamActivityProvider>
        );
    }
    return (
        <TasksAndAlertsUserActivityProvider key="users-activity">
            <UserActivityTableDataWrapper
                rightMenuItems={rightMenuItems}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
            />
        </TasksAndAlertsUserActivityProvider>
    );
}

function TeamActivityTableDataWrapper(props: {
    rightMenuItems: UIMenuItemProps[];
    sortBy: SortBy;
    setSortBy: Dispatch<SetStateAction<SortBy>>;
    sortDirection: UITableSortDirection;
    setSortDirection: Dispatch<SetStateAction<UITableSortDirection>>;
}): ReactElement {
    const { t } = useAnalyticsTranslation();
    const { activity, status, dateString } = useTasksAndAlertsTeamActivity();
    return (
        <InternalActivityTable
            type="teams"
            description={t('components.activity_table.description_teams')}
            data={activity}
            dateString={dateString}
            rightMenuText={t('shared.labels.teams')}
            loading={status === 'pending'}
            error={status === 'error'}
            {...props}
        />
    );
}

function UserActivityTableDataWrapper(props: {
    rightMenuItems: UIMenuItemProps[];
    sortBy: SortBy;
    setSortBy: Dispatch<SetStateAction<SortBy>>;
    sortDirection: UITableSortDirection;
    setSortDirection: Dispatch<SetStateAction<UITableSortDirection>>;
}): ReactElement {
    const { t } = useAnalyticsTranslation();
    const { activity, status, dateString } = useTasksAndAlertsUserActivity();
    return (
        <InternalActivityTable
            type="users"
            description={t('components.activity_table.description_users')}
            data={activity}
            dateString={dateString}
            rightMenuText={t('shared.labels.users')}
            loading={status === 'pending'}
            error={status === 'error'}
            {...props}
        />
    );
}
